import React, { useEffect, useRef, useState } from "react";

interface LazyBackgroundImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
  fallbackColor?: string;
}

export const LazyBackgroundImage: React.FC<LazyBackgroundImageProps> = ({
  src,
  alt,
  className = "",
  style = {},
  children,
  rootMargin = "50px",
  threshold = 0.1,
  fallbackColor = "rgba(0, 0, 0, 0.1)",
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin,
        threshold,
      },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold]);

  useEffect(() => {
    if (!isInView) return;

    const img = new Image();
    img.onload = () => {
      setIsLoaded(true);
    };
    img.onerror = () => {
      setHasError(true);
    };
    img.src = src;
  }, [isInView, src]);

  const backgroundStyle: React.CSSProperties = {
    ...style,
    backgroundImage: isLoaded ? `url(${src})` : undefined,
    backgroundColor: !isLoaded ? fallbackColor : undefined,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    transition: "background-image 0.3s ease-in-out",
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={backgroundStyle}
      role={alt ? "img" : undefined}
      aria-label={alt}
      data-oid="tly:b9."
    >
      {children}
      {hasError && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-500"
          data-oid="u_22-3i"
        >
          <span data-oid="q2d4r-4">Image failed to load</span>
        </div>
      )}
    </div>
  );
};

export default LazyBackgroundImage;
